import React, { createContext, useState, useEffect } from "react";
import { fetchLanguageList } from "../utils/api";
import debounce from 'lodash/debounce';
import { useTranslation } from "react-i18next";
import { getI18nLanguageCode, normalizeLanguageCode } from "../utils/languageOptions";

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
    const [languageList, setLanguageList] = useState([]);
    const [languageLoading, setLanguageLoading] = useState(true);
    const [error, setError] = useState(null); // Changed to null for better error handling
    const { i18n } = useTranslation();

    useEffect(() => {
        const loadLanguages = async () => {
            setLanguageLoading(true);
            setError(null);
            try {
                const response = await fetchLanguageList();
                if (response.success && Array.isArray(response.data) && response.data.length > 0) {
                    setLanguageList(response.data.filter((lang) => lang?.code));
                } else {
                    setLanguageList([]);
                    setError("Failed to fetch languages from API");
                }
            } catch (error) {
                setError(`Error fetching languages: ${error.message}`);
                setLanguageList([]);
            } finally {
                setLanguageLoading(false);
            }
        };

        const debouncedLoadLanguages = debounce(() => {
            loadLanguages();
        }, 1000, { leading: true, trailing: false });

        debouncedLoadLanguages();

        return () => {
            debouncedLoadLanguages.cancel();
        };
    }, []);

    const getLanguageId = () => {
        if (languageLoading || error || !languageList.length) {
            return Number(localStorage.getItem("language_id")) || null;
        }

        const currentLanguage = normalizeLanguageCode(i18n.language);
        const currentI18nLanguage = getI18nLanguageCode(currentLanguage);

        const language = languageList.find(lang => 
            normalizeLanguageCode(lang.code) === currentLanguage ||
            getI18nLanguageCode(lang.code) === currentI18nLanguage ||
            normalizeLanguageCode(lang.name) === currentLanguage
        );
        
        return language ? language.id : null;
    };

    const contextValue = {
        languageList,
        languageLoading,
        error,
        getLanguageId
    };

    return (
        <LanguageContext.Provider value={contextValue}>
            {children}
        </LanguageContext.Provider>
    );
};

export default LanguageContext;